import { Helmet } from 'react-helmet'
import React, { useCallback, useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Loading from '../../../components/loading'
import NotFound from '../../../components/not-found'
import useApi from '../../../hooks/useApi'
import useSponsorPledgeMinimumCiv from '../../../hooks/useSponsorPledgeMinimumCiv'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import civ from '../../../constants/civ'
import SponsorPledgeWizard from './SponsorPledgeWizard'

export default function SponsorPledgeFlowPage() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const { currentUser, business, hasBetaFeature } = useCurrentUser()
  const { getEvent, getAdminFee, createTmpSponsorshipPledge, updateBusiness } =
    useApi()

  const [event, setEvent] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [adminFee, setAdminFee] = useState(civ.empty)
  const [serviceFee, setServiceFee] = useState(civ.empty)

  const fetchEvent = useCallback(async () => {
    if (!slug) return
    try {
      const { data } = await getEvent({ id: slug })
      setEvent(data || null)
      setLoadError(null)
    } catch (e) {
      setLoadError(e?.response?.status ?? 404)
      setEvent(null)
    }
  }, [slug, getEvent])

  useEffect(() => {
    fetchEvent()
  }, [fetchEvent])

  useEffect(() => {
    getAdminFee().then(result => {
      const r = result
      if (r?.data?.current_fees) {
        if (r.data.current_fees.civillos_fee != null) {
          setAdminFee(r.data.current_fees.civillos_fee)
        }
        if (r.data.current_fees.meta_fee != null) {
          setServiceFee(r.data.current_fees.meta_fee)
        }
      }
    })
  }, [getAdminFee])

  const { pledgeEvent, minimumPledgeCiv } = useSponsorPledgeMinimumCiv({
    show: Boolean(event?.id),
    event,
  })

  const canUseFlow =
    currentUser?.id && currentUser?.business_id && hasBetaFeature('pf_business')

  if (!currentUser?.id) {
    return <Navigate to="/login" replace />
  }

  if (!canUseFlow) {
    return <Navigate to={`/events/${slug}`} replace />
  }

  if (loadError === 404) {
    return (
      <NotFound
        type="event"
        showAuthLinks={!currentUser?.id}
        suggestAuth={false}
        redirectTo={`/events/${slug}`}
      />
    )
  }

  if (loadError) {
    return (
      <NotFound
        type="event"
        showAuthLinks={!currentUser?.id}
        suggestAuth={false}
        redirectTo={`/events/${slug}`}
      />
    )
  }

  if (!event) {
    return <Loading show />
  }

  if (!event.volunteer_pay_forward) {
    return <Navigate to={`/events/${event.slug ?? event.id}`} replace />
  }

  return (
    <>
      <Helmet>
        <title>
          {t('sponsorPledgeFlow.pageTitle', {
            event: event.title,
          })}
        </title>
      </Helmet>
      <SponsorPledgeWizard
        event={pledgeEvent || event}
        minimumPledgeCiv={minimumPledgeCiv}
        business={business}
        currentUser={currentUser}
        adminFee={adminFee}
        serviceFee={serviceFee}
        createTmpSponsorshipPledge={createTmpSponsorshipPledge}
        updateBusiness={updateBusiness}
      />
    </>
  )
}
